import * as React from "react";

import { PiletApi } from "opencak-app";

import { Language } from "@cak-legacy/shared-locales";
import {
  createUrl,
  formulierPageContainerClassName,
  PiletApiContextProvider,
  setLanguage
} from "@klantondersteuning-btl/shared";
import { AanvraagDocumentS1Page } from "@page/aanvraag-document-s1";
import {
  AanvraagDocumentS1GezinsledenPage,
  aanvraagDocumentS1GezinsledenUrlPaths
} from "@page/aanvraag-document-s1-gezinsleden";
import { EhicFormulierPage } from "@page/ehic-formulier";
import { AanvragenVozdPage } from "@page/forms-aanvragen-vozd";
import { AdresgegevensWijzigenFormulierPage } from "@page/forms-adresgegevensformulier";
import { EmailWijzigenFormulierPage } from "@page/forms-emailwijzigenformulier";
import { OverlijdenMeldenFormulierPage } from "@page/forms-overlijdenmeldenformulier";
import { SchengenMedicijnverklaringFormulierPage } from "@page/forms-schengen-medicijnverklaring";
import { medicijnOpReisHulpUrlPath, MedicijnOpReisHulpPage } from "@page/medicijn-op-reis-hulp";
import { MedicijnenOpReisFormulierPage } from "@page/medicijnen-op-reis-formulier";
import { SchengenFormulierPage } from "@page/schengen-formulier";

import "./sass/form-base-component.scss";

export function setup(app: PiletApi) {
  const apiPath = app.getData("api-path");
  const basePath = app.getData("base-path");
  const allFeatures = app.allFeaturesToggle();

  const apiUrlBtlKlantondersteuning = createUrl(apiPath, "btl", "klantondersteuning");

  const FormPageContainer = (props: React.PropsWithChildren) => {
    const FormPageLayoutComponent = app.getFormPageLayoutComponent();

    return (
      <PiletApiContextProvider value={app}>
        <FormPageLayoutComponent>{props.children}</FormPageLayoutComponent>
      </PiletApiContextProvider>
    );
  };

  const createEhicFormulierPageComponent = (currentLanguage: string) => () => {
    const pageBaseUrl =
      currentLanguage === Language.Nederlands
        ? createUrl(basePath, "zelf-regelen/formulier-aanvraag-ehic")
        : createUrl(basePath, "self-service/ehic-application-form/en");

    return (
      <FormPageContainer>
        <div className={formulierPageContainerClassName}>
          <EhicFormulierPage
            pageBaseUrl={pageBaseUrl}
            apiUrl={apiUrlBtlKlantondersteuning}
            language={currentLanguage}
          />
        </div>
      </FormPageContainer>
    );
  };

  const createAdresgegevensWijzigenFormulierPageComponent = (currentLanguage: Language) => () => {
    setLanguage(currentLanguage);

    const pageBaseUrl =
      currentLanguage === Language.Nederlands
        ? `zelf-regelen/adresgegevens-wijzigen-formulier`
        : `about/contact/update-addressdetails-form/en`;

    return (
      <FormPageContainer>
        <div className="formulier-page-container-stepper-form">
          <AdresgegevensWijzigenFormulierPage
            basePath={basePath}
            pageBaseUrl={pageBaseUrl}
            apiUrl={apiPath}
            language={currentLanguage}
          />
        </div>
      </FormPageContainer>
    );
  };

  const createEmailWijzigenFormulierPageComponent = (currentLanguage: Language) => () => {
    setLanguage(currentLanguage);

    const pageBaseUrl =
      currentLanguage === Language.Nederlands
        ? `zelf-regelen/email-wijzigen-formulier`
        : `about/contact/update-email-form/en`;

    return (
      <FormPageContainer>
        <div className="formulier-page-container-stepper-form">
          <EmailWijzigenFormulierPage
            basePath={basePath}
            pageBaseUrl={pageBaseUrl}
            apiUrl={apiPath}
            language={currentLanguage}
          />
        </div>
      </FormPageContainer>
    );
  };

  const createAanvragenVozdPageComponent = (currentLanguage: Language, pageBaseUrl: string) => () => {
    setLanguage(currentLanguage);

    return (
      <FormPageContainer>
        <div className="formulier-page-container-stepper-form">
          <AanvragenVozdPage
            basePath={basePath}
            pageBaseUrl={pageBaseUrl}
            apiUrl={apiPath}
            language={currentLanguage}
          />
        </div>
      </FormPageContainer>
    );
  };

  const createOverlijdenMeldenFormulierPageComponent = (currentLanguage: Language) => () => {
    setLanguage(currentLanguage);

    const pageBaseUrl =
      currentLanguage === Language.Nederlands
        ? `zelf-regelen/overlijden-melden-formulier`
        : `about/contact/report-deceased-form/en`;

    return (
      <FormPageContainer>
        <div className="formulier-page-container-stepper-form">
          <OverlijdenMeldenFormulierPage
            basePath={basePath}
            pageBaseUrl={pageBaseUrl}
            apiUrl={apiPath}
            language={currentLanguage}
          />
        </div>
      </FormPageContainer>
    );
  };

  const schengenMedicijnverklaringFormWithCmsContentEnabled = allFeatures.find(
    (feature) => feature.name === "formulieren/schengen-medicijnverklaring-cms"
  )?.enabled;
  const schengenMedicijnverklaringFormNlPath = "zelf-regelen/schengenverklaring-opsturen";

  if (schengenMedicijnverklaringFormWithCmsContentEnabled) {
    const createSchengenVerklaringFormulierPageComponent = (currentLanguage: Language) => () => {
      setLanguage(currentLanguage);

      return (
        <FormPageContainer>
          <div className="formulier-page-container-stepper-form">
            <SchengenMedicijnverklaringFormulierPage
              basePath={basePath}
              pageBaseUrl={schengenMedicijnverklaringFormNlPath}
              apiUrl={apiPath}
              language={currentLanguage}
            />
          </div>
        </FormPageContainer>
      );
    };

    app.registerPage(
      createUrl(basePath, `${schengenMedicijnverklaringFormNlPath}/:formStep?`),
      createSchengenVerklaringFormulierPageComponent(Language.Nederlands)
    );
  } else {
    const SchengenFormulierPageComponent = () => {
      const pageBaseUrl = createUrl(basePath, schengenMedicijnverklaringFormNlPath);

      return (
        <FormPageContainer>
          <div className={formulierPageContainerClassName}>
            <SchengenFormulierPage pageBaseUrl={pageBaseUrl} apiUrl={apiUrlBtlKlantondersteuning} />
          </div>
        </FormPageContainer>
      );
    };

    app.registerPage(
      createUrl(basePath, `${schengenMedicijnverklaringFormNlPath}/:formStep?`),
      SchengenFormulierPageComponent
    );
  }

  const MedicijnenOpReisFormulierPageComponent = () => {
    const pageBaseUrl = createUrl(basePath, medicijnOpReisHulpUrlPath);
    const medicijnOpReisMEBVEnabled = allFeatures.find(
      (feature) => feature.name === "formulieren/medicijn-op-reis-hulp"
    )?.enabled;

    if (medicijnOpReisMEBVEnabled) {
      return (
        <FormPageContainer>
          <MedicijnOpReisHulpPage
            language={Language.Nederlands}
            apiGatewayUrl={apiPath}
            applicationUrl={basePath}
            apiUrl={apiUrlBtlKlantondersteuning}
          />
        </FormPageContainer>
      );
    }

    return (
      <FormPageContainer>
        <div className={formulierPageContainerClassName}>
          <MedicijnenOpReisFormulierPage pageBaseUrl={pageBaseUrl} apiUrl={apiUrlBtlKlantondersteuning} />
        </div>
      </FormPageContainer>
    );
  };

  const createAanvraagDocumentS1FormulierPageComponent = (currentLanguage: Language) => () => {
    setLanguage(currentLanguage);

    const pageBaseUrl =
      currentLanguage === Language.Nederlands
        ? createUrl(basePath, "zelf-regelen/aanvraag-cak-verzekering-in-het-buitenland")
        : createUrl(basePath, "self-service/application-cak-insurance-abroad/en");

    return (
      <FormPageContainer>
        <AanvraagDocumentS1Page
          language={currentLanguage}
          pageBaseUrl={pageBaseUrl}
          apiUrl={apiUrlBtlKlantondersteuning}
          apiUrlGenericKlantondersteuning={apiPath}
        />
      </FormPageContainer>
    );
  };

  const createAanvraagDocumentS1GezinsledenFormulierPageComponent = (currentLanguage: Language) => () => {
    setLanguage(currentLanguage);

    return (
      <FormPageContainer>
        <AanvraagDocumentS1GezinsledenPage
          language={currentLanguage}
          apiGatewayUrl={apiPath}
          applicationUrl={basePath}
        />
      </FormPageContainer>
    );
  };

  app.registerPage(
    createUrl(basePath, "zelf-regelen/formulier-aanvraag-ehic/:formStep?"),
    createEhicFormulierPageComponent(Language.Nederlands)
  );

  app.registerPage(
    createUrl(basePath, "self-service/ehic-application-form/en/:formStep?"),
    createEhicFormulierPageComponent(Language.English)
  );

  app.registerPage(
    createUrl(basePath, "zelf-regelen/adresgegevens-wijzigen-formulier/:formStep?"),
    createAdresgegevensWijzigenFormulierPageComponent(Language.Nederlands)
  );

  app.registerPage(
    createUrl(basePath, "about/contact/update-addressdetails-form/en/:formStep?"),
    createAdresgegevensWijzigenFormulierPageComponent(Language.English)
  );

  app.registerPage(
    createUrl(basePath, "zelf-regelen/email-wijzigen-formulier/:formStep?"),
    createEmailWijzigenFormulierPageComponent(Language.Nederlands)
  );

  app.registerPage(
    createUrl(basePath, "about/contact/update-email-form/en/:formStep?"),
    createEmailWijzigenFormulierPageComponent(Language.English)
  );

  app.registerPage(
    createUrl(basePath, `${medicijnOpReisHulpUrlPath}/:formStep?`),
    MedicijnenOpReisFormulierPageComponent
  );

  app.registerPage(
    createUrl(basePath, "zelf-regelen/aanvraag-cak-verzekering-in-het-buitenland/:formStep?"),
    createAanvraagDocumentS1FormulierPageComponent(Language.Nederlands)
  );

  app.registerPage(
    createUrl(basePath, "self-service/application-cak-insurance-abroad/en/:formStep?"),
    createAanvraagDocumentS1FormulierPageComponent(Language.English)
  );

  const aanvraagS1GezinsledenEnabled = allFeatures.find(
    (feature) => feature.name === "formulieren/aanvraag-document-s1-gezinsleden"
  )?.enabled;

  if (aanvraagS1GezinsledenEnabled) {
    [Language.Nederlands, Language.English].forEach((language) => {
      app.registerPage(
        createUrl(basePath, aanvraagDocumentS1GezinsledenUrlPaths[language], ":formStep?"),
        createAanvraagDocumentS1GezinsledenFormulierPageComponent(language)
      );
    });
  }

  const nlAanvragenVozdPageUrl = "zelf-regelen/btl-aanvragen-vozd";
  app.registerPage(
    createUrl(basePath, `${nlAanvragenVozdPageUrl}/:formStep?`),
    createAanvragenVozdPageComponent(Language.Nederlands, nlAanvragenVozdPageUrl)
  );

  const enAanvragenVozdPageUrl = "self-service/btl-vozd-application-form/en";
  app.registerPage(
    createUrl(basePath, `${enAanvragenVozdPageUrl}/:formStep?`),
    createAanvragenVozdPageComponent(Language.English, enAanvragenVozdPageUrl)
  );

  app.registerPage(
    createUrl(basePath, "zelf-regelen/overlijden-melden-formulier/:formStep?"),
    createOverlijdenMeldenFormulierPageComponent(Language.Nederlands)
  );

  app.registerPage(
    createUrl(basePath, "about/contact/report-deceased-form/en/:formStep?"),
    createOverlijdenMeldenFormulierPageComponent(Language.English)
  );
}
